"use client";

import React, { useState } from "react";
import FlyingBees from "./FlyingBees";

interface FaqItem {
    id: string;
    question: string;
    answer: string;
    icon: string;
}

const faqData: {
    [key: string]: {
        categoryIcon: string;
        items: FaqItem[];
    };
} = {
    "Technik & Sensorik": {
        categoryIcon: "🔧",
        items: [
            {
                id: "tech-1",
                question: "Warum ist es im Bienenstock auch im Winter warm?",
                answer: "Bienen halten die Temperatur im Stock selbst im Winter bei etwa 25–30 °C, indem sie durch Muskelzittern Wärme erzeugen. Temperaturfühler in der Beute zeigen oft, wie konstant und stabil dieses Heizverhalten ist – ein echtes Wunder der Thermoregulation!",
                icon: "🧪",
            },
            {
                id: "tech-2",
                question: "Wie erkennen Sensoren, ob das Bienenvolk gesund ist?",
                answer: "Ein plötzlicher Abfall der Stocktemperatur oder Luftfeuchtigkeit kann auf Krankheiten, das Fehlen der Königin oder Schwarmverhalten hinweisen. Sensoren liefern somit wichtige Frühwarnzeichen für Imker*innen.",
                icon: "📈",
            },
            {
                id: "tech-3",
                question: "Wie beeinflusst das Wetter das Verhalten der Bienen?",
                answer: "Bei hoher Luftfeuchtigkeit und sinkenden Außentemperaturen bleiben viele Bienen im Stock. Sensorik in der Beute hilft, diese klimatischen Schwankungen zu analysieren und Rückschlüsse auf Aktivität und Futternutzung zu ziehen.",
                icon: "🌧️",
            },
            {
                id: "tech-4",
                question: "Können Bienen denken oder nur instinktiv handeln?",
                answer: "Bienen zeigen erstaunlich komplexes Verhalten – sie lernen, navigieren und kommunizieren per Tanz. In Kombination mit Sensordaten wird sichtbar, wie äußere Bedingungen diese Verhaltensweisen beeinflussen.",
                icon: "🧠",
            },
            {
                id: "tech-5",
                question: "Wie regulieren Bienen ihre eigene Wohnung?",
                answer: "Durch Flügelschlagen belüften Bienen den Stock bei Hitze. Temperatur- und Feuchtigkeitssensoren zeigen in Echtzeit, wie effizient die Bienen ihr Mikroklima steuern – oft besser als jede Klimaanlage!",
                icon: "🌬️",
            },
            {
                id: "tech-6",
                question: "Was passiert, wenn die Luftfeuchtigkeit im Stock zu niedrig ist?",
                answer: "Zu trockene Luft kann das Brutklima gefährden. Bienen regulieren aktiv durch Verdunstung. Mit Sensorik lässt sich genau erkennen, wann und wie sie eingreifen – ein faszinierender Einblick in ihr Raumklima-Management.",
                icon: "💧",
            },
            {
                id: "tech-7",
                question: "Warum ist die Temperatur für die Brut so entscheidend?",
                answer: "Die Brut gedeiht nur bei etwa 34–35 °C. Sensoren zeigen, ob das Volk in der Lage ist, diese konstant zu halten – ein wichtiger Indikator für die Volksstärke und Gesundheit.",
                icon: "🎯",
            },
            {
                id: "tech-8",
                question: "Können Sensoren auch helfen, Schwärme vorherzusagen?",
                answer: "Ja! Ein rapider Temperaturanstieg oder eine hohe Luftfeuchtigkeit kann Vorbote eines Schwarms sein. Sensorik macht diese subtilen Vorzeichen frühzeitig sichtbar – bevor der Schwarm davonfliegt.",
                icon: "⚠️",
            },
            {
                id: "tech-9",
                question: "Was sagen Temperaturverläufe über die Tagesaktivität?",
                answer: "Typischerweise steigt die Temperatur morgens leicht an, wenn die Sammelbienen aktiv werden. Ein Sensor-Log zeigt diese Zyklen deutlich – und erlaubt spannende Rückschlüsse auf Wetter, Tageszeit und Bienenverhalten.",
                icon: "🌅",
            },
            {
                id: "tech-10",
                question: "Wie können Bienenstöcke mit Sensoren zur Forschung beitragen?",
                answer: "Beuten mit eingebauter Sensorik liefern wertvolle Langzeitdaten für Imker, Forscher und Naturschützer – zum Beispiel über Klimawandel, Bestäubungsverhalten oder Umweltgifte. Jede gemessene Kurve erzählt eine Geschichte.",
                icon: "🗃️",
            },
        ],
    },
    Bienenwissen: {
        categoryIcon: "🐝",
        items: [
            {
                id: "bee-1",
                question: "Welche Bienenarten gibt es im Stock – und wer hat das Sagen?",
                answer: "In jedem Bienenvolk gibt es drei Typen: Die Königin (1x), Arbeiterinnen (tausende), und Drohnen (wenige hundert). Die Königin legt Eier, die Arbeiterinnen erledigen alle Aufgaben im Stock, und Drohnen sorgen für die Begattung.",
                icon: "🧑‍🤝‍🧑",
            },
            {
                id: "bee-2",
                question: "Wie groß ist ein Bienenvolk eigentlich?",
                answer: "Ein gesundes Sommer-Volk umfasst 40.000 bis 60.000 Bienen! Im Winter schrumpft es auf 10.000–15.000. Sensoren in der Beute erkennen diese Unterschiede oft an der Temperaturverteilung und dem Brutverhalten.",
                icon: "📊",
            },
            {
                id: "bee-3",
                question: "Wie lange lebt eine Biene?",
                answer: "Sommerbienen leben etwa 5–6 Wochen, Winterbienen bis zu 6 Monate. Eine Königin kann bis zu 5 Jahre alt werden – Drohnen hingegen sterben nach der Begattung.",
                icon: "⏳",
            },
            {
                id: "bee-4",
                question: "Wann beginnt ein Bienenleben – und wie lange dauert es zur Biene?",
                answer: "Vom Ei bis zur fertigen Biene vergeht: Arbeiterin: 21 Tage, Drohne: 24 Tage, Königin: nur 16 Tage. Sensoren im Brutraum helfen, das optimale Klima dafür sicherzustellen.",
                icon: "📅",
            },
            {
                id: "bee-5",
                question: "Haben Bienen einen Kalender?",
                answer: "Ja! Bienen folgen einem klaren Jahreszyklus: Frühling (Aufbau), Sommer (Sammeln, Vermehren), Herbst (Sichern), Winter (Ruhen). Sensorik hilft, diese Phasen sichtbar zu machen.",
                icon: "🔄",
            },
            {
                id: "bee-6",
                question: "Wie entsteht eine neue Königin?",
                answer: "Eine Larve wird mit Gelee Royale gefüttert – dadurch entwickelt sie sich zur Königin. Dieser Prozess wird bei Schwarmstimmung ausgelöst und kann durch Sensorik (Temperatur & Aktivität) erkannt werden.",
                icon: "👑",
            },
            {
                id: "bee-7",
                question: "Wie finden Bienen immer wieder nach Hause?",
                answer: "Bienen nutzen Sonne, Magnetfeld und Landschaftsmerkmale zur Navigation. Ihre Ausflüge lassen sich indirekt an Temperaturdifferenzen im Sensorprotokoll erkennen.",
                icon: "🧭",
            },
        ],
    },
};

export default function FaqSection() {
    const [activeCategory, setActiveCategory] = useState<string>("Technik & Sensorik");
    const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

    const toggleQuestion = (id: string) => {
        const newOpenQuestions = new Set(openQuestions);
        if (newOpenQuestions.has(id)) {
            newOpenQuestions.delete(id);
        } else {
            newOpenQuestions.add(id);
        }
        setOpenQuestions(newOpenQuestions);
    };

    return (
        <section className="faq-section relative py-16 px-6" style={{ backgroundColor: "#fefdf8" }}>
            <div className="max-w-6xl mx-auto relative z-10">
                {/* Section Header */}
                <div className="text-center mb-12">
                    <h2 className="text-5xl font-bold text-gray-900 mb-4">Bienenwissen</h2>
                    <p className="text-lg text-amber-600 font-medium">
                        Spannendes rund um Bienen, Sensorik und digitale Technik
                    </p>
                </div>

                {/* Category Tabs */}
                <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-white rounded-full p-1 shadow-md">
                        {Object.entries(faqData).map(([category, { categoryIcon }]) => (
                            <button
                                type="button"
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 flex items-center space-x-2 cursor-pointer ${
                                    activeCategory === category
                                        ? "bg-amber-400 text-white"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-amber-50"
                                }`}
                            >
                                <span className="text-lg">{categoryIcon}</span>
                                <span>{category}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* FAQ Grid */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                    {faqData[activeCategory].items.map((item) => (
                        <div
                            key={item.id}
                            className="group relative bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col"
                        >
                            {/* Question Header */}
                            <button
                                type="button"
                                onClick={() => toggleQuestion(item.id)}
                                className="w-full p-6 flex items-start justify-between text-left focus:outline-none hover:bg-amber-50 cursor-pointer transition-colors duration-200 min-h-[110px]"
                            >
                                <div className="flex items-start space-x-4 flex-1">
                                    <span className="text-2xl flex-shrink-0">{item.icon}</span>
                                    <h3 className="text-lg font-semibold text-gray-900 flex-1">{item.question}</h3>
                                </div>
                                <span
                                    className={`flex-shrink-0 ml-4 text-amber-600 transform transition-transform duration-200 ${
                                        openQuestions.has(item.id) ? "rotate-180" : ""
                                    }`}
                                >
                                    ▼
                                </span>
                            </button>

                            {/* Answer Content */}
                            {openQuestions.has(item.id) && (
                                <div className="animate-in slide-in-from-top-2 duration-300">
                                    <div className="p-6 pt-0 text-gray-700 border-t border-gray-100">
                                        <p className="leading-relaxed">{item.answer}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <FlyingBees
                bees={[
                    { top: "10%", left: "85%", width: 75 },
                    { top: "30%", left: "8%", width: 85 },
                    { top: "50%", left: "92%", width: 80 },
                    { top: "70%", left: "15%", width: 90 },
                    { top: "90%", left: "75%", width: 70 },
                ]}
            />
        </section>
    );
}
